import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import {
    LEGAL_DEADLINE_TYPE_OPTIONS,
    LegalDeadlineType,
    type LegalDeadline,
    type SelectOption,
} from '../types/LegalCase';
import type { ErrorField } from '../types/ErrorField';

interface AddDeadlineValue {
    deadlineType: LegalDeadlineType;
    dueDate: string;
    description: string;
}

// Gerencia os prazos processuais de um caso: lista, adiciona, marca cumprido e remove.
// Fetch próprio pelo id do caso. Mesmo padrão do useLegalCaseMeasures.
export default function useLegalCaseDeadlines(caseId: string | null, initialDeadlines?: LegalDeadline[]) {
    const [deadlines, setDeadlines] = useState<LegalDeadline[]>(initialDeadlines || []);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);
    const [typeOptions, setTypeOptions] = useState<SelectOption[]>(LEGAL_DEADLINE_TYPE_OPTIONS);
    const seededCaseRef = useRef<string | null>(null);

    const fetchDeadlines = useCallback(async () => {
        if (!caseId) return;
        setIsLoading(true);
        try {
            const result = await recoveryApi.getDeadlines(caseId);
            setDeadlines(result.items || []);
        } catch {
            // silencioso — a seção já mostra os prazos que vieram no page-view
        } finally {
            setIsLoading(false);
        }
    }, [caseId]);

    useEffect(() => {
        if (seededCaseRef.current === caseId) return;
        seededCaseRef.current = caseId;
        setDeadlines(initialDeadlines || []);
        setErrorFields([]);
        // initialDeadlines fora das deps de propósito (referência nova a cada render).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId]);

    useEffect(() => {
        const loadTypes = async () => {
            try {
                const opts = await recoveryApi.getLegalCaseFormOptions();
                if (opts.legalDeadlineTypes?.length) setTypeOptions(opts.legalDeadlineTypes);
            } catch {
                // fallback local já preenche
            }
        };
        loadTypes();
    }, []);

    const clearErrors = () => setErrorFields([]);

    const addDeadline = async (data: AddDeadlineValue): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.addDeadline(caseId, {
                deadlineType: data.deadlineType,
                dueDate: data.dueDate,
                description: data.description || undefined,
            });
            await fetchDeadlines();
            toast.success('Prazo registrado');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            if (e instanceof ApiError && e.status === 404) {
                toast.error(e.data?.message || 'Caso não encontrado');
                return false;
            }
            toast.error(e?.message || 'Erro ao registrar prazo');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const setFulfilled = async (deadlineId: string, fulfilled: boolean) => {
        if (!caseId) return;
        setIsSaving(true);
        try {
            await recoveryApi.updateDeadline(caseId, deadlineId, { fulfilled });
            await fetchDeadlines();
            toast.success(fulfilled ? 'Prazo marcado como cumprido' : 'Prazo reaberto');
        } catch (e: any) {
            toast.error(e?.data?.message || e?.message || 'Erro ao atualizar prazo');
        } finally {
            setIsSaving(false);
        }
    };

    const removeDeadline = async (deadlineId: string) => {
        if (!caseId) return;
        setIsSaving(true);
        try {
            await recoveryApi.removeDeadline(caseId, deadlineId);
            await fetchDeadlines();
            toast.success('Prazo removido');
        } catch (e: any) {
            toast.error(e?.data?.message || e?.message || 'Erro ao remover prazo');
        } finally {
            setIsSaving(false);
        }
    };

    return {
        deadlines, isLoading, isSaving, errorFields, typeOptions,
        addDeadline, setFulfilled, removeDeadline, clearErrors,
    };
}

export type { AddDeadlineValue };
